import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, ReferenceArea, Legend, Area, ComposedChart
} from 'recharts';
import { TrendingUp, TrendingDown, RefreshCw, Activity, Target, BarChart3, ShieldCheck, AlertTriangle, Info } from 'lucide-react';
import useTodayVwap from "../hooks/useTodayVwap";

import { BACKEND_URL } from "../config/api";

const API = BACKEND_URL;

export default function PremarketPatternChart({ ticker }) {
  const vwap = useTodayVwap(ticker);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lookback, setLookback] = useState('1mo');

  const fetchData = useCallback(async () => {
    if (!ticker) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/api/stock/premarket-pattern/${ticker}`, {
        params: { lookback },
        timeout: 60000
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching premarket pattern:', err);
      setError(err.response?.data?.detail || 'Failed to load premarket pattern data');
    } finally {
      setLoading(false);
    }
  }, [ticker, lookback]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find all relevant data points
      const avgData = payload.find(p => p.dataKey === 'avg');
      const minData = payload.find(p => p.dataKey === 'min');
      const maxData = payload.find(p => p.dataKey === 'max');
      const p25Data = payload.find(p => p.dataKey === 'p25');
      const p75Data = payload.find(p => p.dataKey === 'p75');
      const todayData = payload.find(p => p.dataKey === 'today');
      
      return (
        <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-2 shadow-lg">
          <p className="text-xs text-gray-400 mb-1 font-medium">{label}</p>
          {avgData && avgData.value !== null && (
            <p className="text-xs" style={{ color: '#ffffff' }}>
              Avg: {avgData.value?.toFixed(3)}%
            </p>
          )}
          {p75Data && p75Data.value !== null && (
            <p className="text-xs" style={{ color: '#06b6d4' }}>
              Typical High (75%): {p75Data.value?.toFixed(3)}%
            </p>
          )}
          {p25Data && p25Data.value !== null && (
            <p className="text-xs" style={{ color: '#06b6d4' }}>
              Typical Low (25%): {p25Data.value?.toFixed(3)}%
            </p>
          )}
          {maxData && maxData.value !== null && (
            <p className="text-xs" style={{ color: '#f97316' }}>
              Extreme Max: {maxData.value?.toFixed(3)}%
            </p>
          )}
          {minData && minData.value !== null && (
            <p className="text-xs" style={{ color: '#f97316' }}>
              Extreme Min: {minData.value?.toFixed(3)}%
            </p>
          )}
          {todayData && todayData.value !== null && (
            <p className="text-xs font-medium mt-1 pt-1 border-t border-gray-700" style={{ color: '#e879f9' }}>
              Today: {todayData.value?.toFixed(3)}%
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Prepare chart data
  const prepareChartData = (patternData, todayData) => {
    if (!patternData?.time_series) return [];
    
    const todayMap = {};
    if (todayData?.time_series) {
      todayData.time_series.forEach(p => {
        todayMap[p.time_minutes] = p.pct_change;
      });
    }
    
    return patternData.time_series.map(point => ({
      time: point.time_label,
      time_minutes: point.time_minutes,
      avg: point.avg_pct_change,
      min: point.min_pct_change,
      max: point.max_pct_change,
      p25: point.p25_pct_change,  // 25th percentile - typical lower bound
      p75: point.p75_pct_change,  // 75th percentile - typical upper bound
      today: todayMap[point.time_minutes] ?? null,
    }));
  };

  const renderChart = (patternData, title, direction, isToday) => {
    const chartData = prepareChartData(patternData, isToday ? data?.today : null);
    const stats = patternData?.stats || {};
    
    if (!chartData.length) {
      return (
        <div className="text-center py-8 text-gray-500 text-sm">
          <Activity className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No data available for {title.toLowerCase()}
        </div>
      );
    }

    // Calculate Y-axis domain to show full volatility range
    const allValues = chartData.flatMap(d => [
      d.min, d.max, d.avg, d.today, d.p25, d.p75
    ]).filter(v => v !== null && v !== undefined && !isNaN(v));
    
    const yMin = Math.min(...allValues, stats.support_level || 0) - 0.3;
    const yMax = Math.max(...allValues, stats.resistance_level || 0) + 0.3;

    // Calculate regression line points
    const regressionStart = stats.regression?.intercept || 0;
    const regressionEnd = regressionStart + (stats.regression?.slope || 0) * (chartData[chartData.length - 1]?.time_minutes || 0);

    return (
      <div className="space-y-2">
        {/* Chart Header with Stats */}
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            {direction === 'positive' ? (
              <TrendingUp className="w-4 h-4 text-green-400" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-400" />
            )}
            <span className="text-sm font-semibold text-white">{title}</span>
            <span className="text-[10px] text-gray-500">
              ({patternData?.day_count || 0} days)
            </span>
            {/* Statistical Confidence Badge */}
            {stats.confidence && (
              <span 
                className={`text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1 ${
                  stats.confidence.level === 'high' 
                    ? 'bg-green-900/40 text-green-400 border border-green-700/50'
                    : stats.confidence.level === 'good'
                    ? 'bg-blue-900/40 text-blue-400 border border-blue-700/50'
                    : stats.confidence.level === 'moderate'
                    ? 'bg-yellow-900/40 text-yellow-400 border border-yellow-700/50'
                    : stats.confidence.level === 'low'
                    ? 'bg-orange-900/40 text-orange-400 border border-orange-700/50'
                    : 'bg-red-900/40 text-red-400 border border-red-700/50'
                }`}
                title={stats.confidence.description}
              >
                {stats.confidence.level === 'high' || stats.confidence.level === 'good' ? (
                  <ShieldCheck className="w-3 h-3" />
                ) : stats.confidence.level === 'moderate' ? (
                  <Info className="w-3 h-3" />
                ) : (
                  <AlertTriangle className="w-3 h-3" />
                )}
                {stats.confidence.level === 'high' ? 'High Confidence' :
                 stats.confidence.level === 'good' ? 'Good Confidence' :
                 stats.confidence.level === 'moderate' ? 'Moderate' :
                 stats.confidence.level === 'low' ? 'Low Confidence' : 'Insufficient Data'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[10px]">
            <span className="text-gray-400">
              Win Rate: <span className={stats.win_rate > 50 ? 'text-green-400' : 'text-red-400'}>{stats.win_rate}%</span>
            </span>
            <span className="text-gray-400">
              Avg High: <span className="text-green-400">+{stats.avg_daily_high}%</span>
            </span>
            <span className="text-gray-400">
              Avg Low: <span className="text-red-400">{stats.avg_daily_low}%</span>
            </span>
          </div>
        </div>

        {/* Pattern Consistency Info */}
        {stats.consistency && stats.consistency.level !== 'unknown' && (
          <div className="mx-2 text-[9px] text-gray-500 flex items-center gap-2">
            <span className={`px-1.5 py-0.5 rounded ${
              stats.consistency.level === 'very_consistent' ? 'bg-green-900/20 text-green-500' :
              stats.consistency.level === 'consistent' ? 'bg-blue-900/20 text-blue-400' :
              stats.consistency.level === 'variable' ? 'bg-yellow-900/20 text-yellow-400' :
              'bg-red-900/20 text-red-400'
            }`}>
              {stats.consistency.description}
            </span>
            <span className="text-gray-600">
              (σ = {stats.consistency.avg_std}%)
            </span>
          </div>
        )}

        {/* Correlation Badge (if today matches this pattern) */}
        {isToday && data?.correlation?.[direction] && (
          <div className={`mx-2 px-2 py-1 rounded text-[10px] ${
            data.correlation[direction].strength === 'strong' 
              ? 'bg-green-900/30 border border-green-700 text-green-400'
              : data.correlation[direction].strength === 'moderate'
              ? 'bg-yellow-900/30 border border-yellow-700 text-yellow-400'
              : 'bg-gray-800 border border-gray-700 text-gray-400'
          }`}>
            <Target className="w-3 h-3 inline mr-1" />
            Correlation to historical: <strong>{(data.correlation[direction].correlation * 100).toFixed(1)}%</strong>
            {' '}({data.correlation[direction].strength})
          </div>
        )}

        {/* Chart with Volatility Bands — explicit height prevents Recharts
            width(-1)/height(-1) warnings on first paint */}
        <div>
          <ResponsiveContainer width="100%" height={250} debounce={50}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                {/* Gradient for typical range (25th-75th percentile) */}
                <linearGradient id={`typicalRangeGradient-${direction}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="time" 
                tick={{ fill: '#888', fontSize: 9 }}
                interval="preserveStartEnd"
                tickFormatter={(value) => value}
              />
              <YAxis 
                domain={[yMin, yMax]}
                tick={{ fill: '#888', fontSize: 9 }}
                tickFormatter={(value) => `${value.toFixed(1)}%`}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '10px' }}
                iconSize={8}
              />
              
              {/* Support and Resistance Areas */}
              {stats.resistance_level && (
                <ReferenceArea
                  y1={stats.resistance_level}
                  y2={yMax}
                  fill="#22c55e"
                  fillOpacity={0.1}
                  label={{ value: `R: ${stats.resistance_level}%`, position: 'right', fill: '#22c55e', fontSize: 9 }}
                />
              )}
              {stats.support_level && (
                <ReferenceArea
                  y1={yMin}
                  y2={stats.support_level}
                  fill="#ef4444"
                  fillOpacity={0.1}
                  label={{ value: `S: ${stats.support_level}%`, position: 'right', fill: '#ef4444', fontSize: 9 }}
                />
              )}
              
              {/* Zero line */}
              <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
              
              {/* Regression line */}
              {stats.regression && (
                <ReferenceLine
                  segment={[
                    { x: chartData[0]?.time, y: regressionStart },
                    { x: chartData[chartData.length - 1]?.time, y: regressionEnd }
                  ]}
                  stroke="#f59e0b"
                  strokeWidth={1}
                  strokeDasharray="5 5"
                />
              )}
              
              {/* Extreme Range - Min/Max (dashed outer boundary) - ORANGE */}
              <Line 
                type="monotone" 
                dataKey="min" 
                name="Extreme Min"
                stroke="#f97316"
                strokeWidth={1}
                strokeDasharray="4 3"
                strokeOpacity={0.6}
                dot={false}
                activeDot={false}
              />
              <Line 
                type="monotone" 
                dataKey="max" 
                name="Extreme Max"
                stroke="#f97316"
                strokeWidth={1}
                strokeDasharray="4 3"
                strokeOpacity={0.6}
                dot={false}
                activeDot={false}
              />
              
              {/* Typical Range - 25th/75th Percentile - CYAN/TEAL */}
              <Line 
                type="monotone" 
                dataKey="p25" 
                name="Typical Low (25%)"
                stroke="#06b6d4"
                strokeWidth={1.5}
                strokeOpacity={0.9}
                dot={false}
                activeDot={false}
              />
              <Line 
                type="monotone" 
                dataKey="p75" 
                name="Typical High (75%)"
                stroke="#06b6d4"
                strokeWidth={1.5}
                strokeOpacity={0.9}
                dot={false}
                activeDot={false}
              />
              
              {/* Historical Average Line (White) */}
              <Line 
                type="monotone" 
                dataKey="avg" 
                name="Historical Avg"
                stroke="#ffffff" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#ffffff' }}
              />
              
              {/* Today's Line (BRIGHT MAGENTA/PINK) */}
              {isToday && (
                <Line 
                  type="monotone" 
                  dataKey="today" 
                  name="Today"
                  stroke="#e879f9" 
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, fill: '#e879f9' }}
                  connectNulls={false}
                />
              )}

              {/* Today's VWAP — violet horizontal reference (% from open) */}
              {vwap.hasData && vwap.latestPct != null && (
                <ReferenceLine
                  y={vwap.latestPct}
                  stroke="#ee82ee"
                  strokeDasharray="4 3"
                  strokeWidth={2}
                  label={{ value: `VWAP ${vwap.latestPct >= 0 ? "+" : ""}${vwap.latestPct.toFixed(3)}%`, fontSize: 10, fill: '#ee82ee', position: 'right' }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Trend Info */}
        <div className="flex items-center justify-between px-2 text-[10px] text-gray-500">
          <span>
            Regression Trend: 
            <span className={`ml-1 font-medium ${
              stats.regression?.trend === 'bullish' ? 'text-green-400' :
              stats.regression?.trend === 'bearish' ? 'text-red-400' : 'text-gray-400'
            }`}>
              {stats.regression?.trend?.toUpperCase() || 'N/A'}
            </span>
            {stats.regression?.slope && (
              <span className="ml-1">({stats.regression.slope > 0 ? '+' : ''}{(stats.regression.slope * 100).toFixed(3)}%/min)</span>
            )}
          </span>
          <span>
            Avg Close: 
            <span className={`ml-1 font-medium ${stats.avg_close_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.avg_close_pct >= 0 ? '+' : ''}{stats.avg_close_pct}%
            </span>
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-white">Premarket Pattern Analysis</span>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <div key={i} className="bg-[#111] rounded-lg p-4 animate-pulse">
              <div className="h-4 w-40 bg-gray-700 rounded mb-4" />
              <div className="h-[250px] bg-gray-800/50 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#111] rounded-lg p-4 text-center">
        <p className="text-red-400 text-sm">{error}</p>
        <button 
          onClick={fetchData}
          className="mt-2 px-3 py-1 bg-red-900/30 hover:bg-red-900/50 rounded text-xs text-red-400"
        >
          Retry
        </button>
      </div>
    );
  }

  const todayDirection = data?.today?.premarket_direction;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-white">Premarket Pattern Analysis</span>
          {todayDirection && (
            <span className={`text-[10px] px-2 py-0.5 rounded ${
              todayDirection === 'positive' 
                ? 'bg-green-900/30 text-green-400 border border-green-700' 
                : 'bg-red-900/30 text-red-400 border border-red-700'
            }`}>
              Today: {todayDirection === 'positive' ? '+' : ''}{data?.today?.premarket_change_pct?.toFixed(2)}% premarket
            </span>
          )}
          <span className={`text-[10px] px-2 py-0.5 rounded ${
            data?.market_status === 'open' 
              ? 'bg-green-900/30 text-green-400' 
              : 'bg-gray-800 text-gray-400'
          }`}>
            Market {data?.market_status}
          </span>
        </div>
        
        {/* Lookback Period Buttons */}
        <div className="flex items-center gap-1 bg-[#0d0d0d] rounded-lg p-1">
          {[
            { key: '5d', label: '5D' },
            { key: '10d', label: '10D' },
            { key: '1mo', label: '1M' },
            { key: '3mo', label: '3M' },
            { key: '6mo', label: '6M' },
            { key: '1y', label: '1Y' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setLookback(key)}
              className={`px-2 py-1 text-[10px] font-medium rounded transition-all ${
                lookback === key
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              data-testid={`premarket-lookback-${key}`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={fetchData}
            disabled={loading}
            className="ml-1 p-1 text-gray-400 hover:text-white"
            data-testid="refresh-premarket-pattern"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Description */}
      <p className="text-[10px] text-gray-500 px-1">
        Compare today's intraday movement (pink) against historical patterns (white). Cyan = typical range (25-75%), 
        Orange dashed = extreme range, Amber dashed = regression trend line.
      </p>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Positive Premarket Chart */}
        <div className="bg-[#111] rounded-lg p-3 border border-gray-800">
          {renderChart(
            data?.positive_premarket, 
            'Positive Premarket Trend',
            'positive',
            todayDirection === 'positive'
          )}
        </div>
        
        {/* Negative Premarket Chart */}
        <div className="bg-[#111] rounded-lg p-3 border border-gray-800">
          {renderChart(
            data?.negative_premarket, 
            'Negative Premarket Trend',
            'negative',
            todayDirection === 'negative'
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 text-[10px] text-gray-500 flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-white rounded"></span> Historical Avg
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-fuchsia-400 rounded"></span> Today
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-cyan-500 rounded"></span> Typical Range (25-75%)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-orange-500 rounded" style={{borderStyle: 'dashed'}}></span> Extreme Range
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-amber-500 rounded" style={{borderStyle: 'dashed'}}></span> Regression
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-green-500/20 border border-green-600 rounded"></span> Resistance
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-red-500/20 border border-red-600 rounded"></span> Support
        </span>
      </div>
    </div>
  );
}
